/**
 * ApiRetryHandler
 *
 * Handles API call retries with exponential backoff for intermittent connectivity scenarios.
 * Used specifically for write visit operations to ensure GPS coordinates are not lost.
 */
class ApiRetryHandler {
    constructor(options = {}) {
        this.maxRetries = options.maxRetries || 3;
        this.retryDelay = options.retryDelay || 1500;
        this.context = options.context || 'API';
        this.useExponentialBackoff = options.useExponentialBackoff !== false; // Default: true
    }

    /**
     * Execute API call with automatic retry on network errors
     * @param {Function} apiCallFn - Function that returns a Promise (the API call)
     * @returns {Promise} - Resolves with API response or rejects after all retries exhausted
     */
    async execute(apiCallFn) {
        let lastError;

        for (let attempt = 0; attempt <= this.maxRetries; attempt++) {
            try {
                console.log(`🔄 [${this.context}] Attempt ${attempt + 1}/${this.maxRetries + 1}`);

                const response = await apiCallFn();

                // Success - return immediately
                console.log(`✅ [${this.context}] Success on attempt ${attempt + 1}`);
                return response;

            } catch (error) {
                lastError = error;
                const isNetworkError = this.isRetryableError(error);

                console.warn(`⚠️ [${this.context}] Attempt ${attempt + 1} failed:`, {
                    status: error.status,
                    message: error.message,
                    isNetworkError,
                    willRetry: isNetworkError && attempt < this.maxRetries
                });

                // Don't retry on business logic errors (400, 403, etc.)
                if (!isNetworkError) {
                    console.error(`❌ [${this.context}] Non-retryable error - stopping retries`);
                    throw error;
                }

                // Don't wait after the last attempt
                if (attempt < this.maxRetries) {
                    // Calculate delay based on backoff strategy
                    const delay = this.useExponentialBackoff
                        ? this.retryDelay * Math.pow(2, attempt)  // Exponential: 1.5s, 3s, 6s, 12s...
                        : this.retryDelay;                         // Fixed: 1.5s, 1.5s, 1.5s...

                    console.log(`⏳ [${this.context}] Waiting ${delay}ms before retry...`);
                    await this.sleep(delay);
                }
            }
        }

        // All retries exhausted
        console.error(`❌ [${this.context}] All ${this.maxRetries + 1} attempts failed`);
        throw lastError;
    }

    /**
     * Determine if an error is retryable (network/timeout errors)
     * @param {Error} error - Error object from API call
     * @returns {boolean} - true if error is retryable
     */
    isRetryableError(error) {
        // Network timeout/connectivity errors (500, 502, 503, 504, network failure)
        const retryableStatuses = [500, 502, 503, 504, 0]; // 0 = network disconnected

        if (error.status && retryableStatuses.includes(error.status)) {
            return true;
        }

        // Check error message for common network error patterns
        const networkErrorPatterns = [
            'network',
            'timeout',
            'ECONNREFUSED',
            'ETIMEDOUT',
            'Failed to fetch',
            'NetworkError'
        ];

        const errorMessage = (error.message || '').toLowerCase();
        return networkErrorPatterns.some(pattern =>
            errorMessage.includes(pattern.toLowerCase())
        );
    }

    /**
     * Sleep utility for delays between retries
     * @param {number} ms - Milliseconds to sleep
     * @returns {Promise}
     */
    sleep(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
}

export default ApiRetryHandler;
